import { AxiosResponse } from 'axios';

import { AddRemoveItemsResponse, GetCollectionItemsResponse } from '~src/app/spaces/Collections/ResourceQuery/CollectionItemsResourceQuery';
import { GetFieldValuesResponse } from '~src/app/spaces/Collections/ResourceQuery/FieldValuesQuery';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import {
  AddRemoveCollectionItemsData,
  Collection,
  CollectionContentItem,
  CollectionError,
  CollectionItem,
  DeleteCollectionData,
  UpdateCollectionsResponseData
} from '../../app/spaces/Collections/CollectionModels';
import { ICollectionInfo } from '../../app/spaces/Collections/ICollectionInfo';
import { IGetCollectionResult } from '../../app/spaces/Collections/IGetCollectionResult';
import {
  CreateCollectionResponse,
  DeleteResponse,
  GetCollectionResponse,
  GetCollectionsResponse,
  UpdateResponse,
} from '../../app/spaces/Collections/ResourceQuery/CollectionsResourceQuery';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ListResult } from '../fetchers/ListResult';
import { IContentInfo } from '../models/IContentInfo';
import { Set } from '../models/Set';
import { mapContentFromCrass } from '../resources/Search';
import { IFavorite } from '../ServerApi/types/IFavorite';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { MapperHelper } from './MapperHelper';
import { UserMapper } from './UserMapper';

export module CollectionMapper {
  function mapCollectionToCollectionInfo(collection: Collection): ICollectionInfo {
    return {
      luid: collection.luid,
      id: collection.id + '',
      name: collection.name,
      description: collection.description,
      contentType: 'collection',
      totalItemCount: collection.totalItemCount,
      permissionedItemCount: collection.permissionedItemCount,
      userId: collection.userId,
      ownerName: collection.ownerName,
      visibility: collection.visibility,
      owner: FeatureFlags.collectionsGAEnabled
        // mapOneOwnerFromCRASS expects `userId` to be called `ownerId` so map that field here
        ? UserMapper.mapOneOwnerFromCRASS({...collection, ownerId: collection.userId})
        : undefined,
      createdAt: MapperHelper.toDate(collection.createdTime),
      updatedAt: MapperHelper.toDate(collection.updatedTime),
      favorite: collection.favorite
    };
  }

  export function mapGetCollectionResult(response: AxiosResponse<GetCollectionResponse>): IGetCollectionResult {
    return {
      collection: mapCollectionToCollectionInfo(response.data.collection),
      errors: response.data.errors ? response.data.errors.map(error => ({ code: error.errorCode })) : [],
    };
  }

  export function mapsSingle(response: AxiosResponse<CreateCollectionResponse>): ICollectionInfo {
    return mapCollectionToCollectionInfo(response.data);
  }

  export function mapList(response: AxiosResponse<GetCollectionsResponse>, start: number): ListResult<ICollectionInfo> {
    return {
      items: response.data.items.map(mapCollectionToCollectionInfo),
      total: response.data.totalCount,
      index: start,
      errors: response.data.errors.map(error => ({
        code: error.errorCode
      })),
    };
  }

  export function mapFromCRASS(content: any): ICollectionInfo {
    return {
      contentType: 'collection',
      luid: content.luid,
      name: content.name,
      id: content.id + '',
      userId: content.ownerId,
      ownerName: content.ownerName,
      totalItemCount: content.itemNames.length,
      permissionedItemCount: -1, // We don't know when fetching collections from crass
      visibility: content.visibility,
      description: content.description,
      createdAt: MapperHelper.toDate(content.createdTime) || MapperHelper.toDateFromTimeOffsetObject(content.createdTime),
      updatedAt: MapperHelper.toDate(content.modifiedTime) || MapperHelper.toDateFromTimeOffsetObject(content.modifiedTime),
      favorite: false,
      owner: UserMapper.mapOneOwnerFromCRASS(content)
    };
  }

  export function mapAddRemoveItemsResponse(response: AxiosResponse<AddRemoveItemsResponse>, luid?: string): AddRemoveCollectionItemsData {
    return {
      numItemsSuccessfullyUpdated: response.data.response,
      errors: response.data.errors,
      collectionLuid: luid,
    };
  }

  export function mapItemsList(response: AxiosResponse<GetCollectionItemsResponse>, start: number, size: number, favorites: _.Dictionary<IFavorite>): ListResult<CollectionContentItem> {
    const contentItems = response.data.items.filter((item) => item.content);
    contentItems.forEach(item => {
      if(item.content.contentType === 'project') {
        item.content.workbookCount = item.extraData?.workbook ?? 0;
        item.content.viewCount = item.extraData?.view ?? 0;
        item.content.childProjectCount = item.extraData?.project ?? 0;
        item.content.datasourceCount = item.extraData?.datasource ?? 0;
      }
    });
    return {
      items: contentItems
        .map(item => ({
          ...mapContentFromCrass(item),
          position: item.position,
          collectionLuid: item.collectionLuid,
          favorite: isFavorite(item, favorites)
        })),
      total: response.data.totalCount,
      index: start,
      errors: response.data.errors ? response.data.errors.map(error => ({ code: error.errorCode })) : [],
    };
  }

  export function mapFieldValuesList(response: AxiosResponse<GetFieldValuesResponse>): ListResult<IFieldValue> {
    const fieldValues = response.data;
    return {
      items: fieldValues.map(fieldValue => ({
        value: fieldValue.value,
        label: fieldValue.label,
        count: fieldValue.count
      })),
      total: fieldValues.length,
      index: 0,
      errors: []
    };
  }

  export function isFavorite(item: CollectionItem, favorites: _.Dictionary<IFavorite>) {
    if (favorites[item.contentLuid]) {
      return true;
    }

    // Data source collection items return datasourceLuid as the contentLuid
    // favorites return the other data source luid, so check favorites map
    // by the regular luid
    if(item.content.contentType==='unifieddatasource') {
      return !!favorites[item.content.luid];
    }

    return false;
  }

  function mapCollectionErrorToIError(collectionError: CollectionError, collection: IContentInfo): IError {
    return {
      code: collectionError.errorCode,
      id: collection?.id
    };
  }

  export function mapDeleteCollectionsDataToIResult(data: DeleteCollectionData, collections: Set<IContentInfo>): IResult {
    const mappedErrors = data.errors.map(err => mapCollectionErrorToIError(err, collections.filter((col: any) => err.luid === col.luid).getByIndex(0)));
    return {
      errors: mappedErrors
    };
  }

  export function mapDeleteCollectionsResponse(response: AxiosResponse<DeleteResponse>): DeleteCollectionData {
    return {
      successfulLuids: response.data.response,
      errors: response.data.errors
    };
  }

  export function mapUpdateCollectionsResponseDataToIResult(data: UpdateCollectionsResponseData, collections: Set<IContentInfo>): IResult {
    const mappedErrors = data.errors.map(err => mapCollectionErrorToIError(err, collections.filter((col: any) => err.luid === col.luid).getByIndex(0)));
    return {
      errors: mappedErrors
    };
  }

  export function mapUpdateCollectionsResponse(response: AxiosResponse<UpdateResponse>): UpdateCollectionsResponseData {
    return {
      successfulLuids: response.data.response,
      errors: response.data.errors
    };
  }
}
